import { Box, Button, FormControlLabel, Radio, RadioGroup, TextareaAutosize, Typography } from '@mui/material'
import React, { useRef, useState } from 'react'
import useStyles from './KaraokeStyles';
import srtParser2 from 'srt-parser-2';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import KaraokeForm from './KaraokeForm';

const UploadLyrics = ({
    lyrics,
    setLyrics,
    setLines,
    setSyncingLyrics,
    setLineTimestamps,
    setFinishedSyncing,
    setCurrentTimestamp,
    uploadedFileName,
    setUploadedFileName,
    updateAudioTime,
    pauseAudio,
    setKaraokeForm,
    setIsEnabled,
    isEnabled,
    karaokeForm,
    backingDuration,
    onVocalUpload,
    onVocalRemove,
    stickers,
    onStickerUpload,
    onAddLibrarySticker,
    onRemoveSticker,
    onClearStickers,
    themeContext = null,
    themeOptions = null,
    onPickTheme,
    onEditAssignments,
    onSavePreview,
    canSavePreview,
    isEdit = false,
}) => {
    const [lyricsOption, setLyricsOption] = useState(() => (lyrics ? 'paste' : 'upload'));

    const srtInputRef = useRef(null);
    const classes = useStyles();
    function handleSrtUpload(e) {
        setLyrics('')
        const file = e.target.files[0];
        setUploadedFileName(file.name);
        const reader = new FileReader();
        reader.onload = () => {
            const parser = new srtParser2();
            const srtArray = parser.fromSrt(reader.result);

            setLyrics(srtArray.map((line) => line.text).join('\n'));
            setLines(srtArray.map((line) => line.text));
            // setSyncingLyrics(true);

            const lineTimestampsData = {};

            srtArray.forEach((line, index) => {
                lineTimestampsData[index] = {
                    start: line.startSeconds === 0 ? 0.1 : line.startSeconds,
                    end: line.endSeconds === 0 ? 0.1 : line.endSeconds,
                };
            });

            setLineTimestamps(lineTimestampsData);
            setFinishedSyncing(true);
            setCurrentTimestamp(0.1);
        };
        reader.readAsText(file);
    }

    const handleLyricsChange = (e) => {
        setUploadedFileName("")
        setLineTimestamps({})
        setLyrics(e.target.value);
        const lines = e.target.value.split('\n');
        setLines(lines);
    };

    const handleRadioChange = (e) => {
        const value = e.target.value
        setLyricsOption(value)
        if (value === 'paste') {
            setLyrics('')
            setLineTimestamps({})
            setUploadedFileName('')
        }
    }
    return (
        <>
            <Typography sx={{ marginTop: '60px' }}>
                Choose an option to add lyrics to the audio
            </Typography>
            <RadioGroup
                value={lyricsOption}
                onChange={handleRadioChange}
                row
                sx={{ marginTop: '10px' }}
            >
                <FormControlLabel value="upload" control={<Radio />} label="Upload SRT file" />
                <FormControlLabel value="paste" control={<Radio />} label="Paste your lyrics" />
            </RadioGroup>

            {lyricsOption === 'paste' ? (
                <TextareaAutosize
                    className={classes.textarea}
                    minRows={11}
                    placeholder="Paste your lyrics here..."
                    value={lyrics}
                    onChange={handleLyricsChange}
                />
            ) : (
                <Box className={classes.uploadBox} onClick={() => srtInputRef.current.click()}>
                    {uploadedFileName ? (
                        <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
                            <MusicNoteIcon className={classes.uploadIcon} />
                            <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                                {uploadedFileName}
                            </Typography>
                            <Typography
                                // onClick={() => srtInputRef.current.click()}
                                sx={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    color: '#142C5C',
                                    marginTop: '10px',
                                    '&:hover': {
                                        color: '#5A7CAD',
                                    },
                                }}>
                                Replace Audio File
                            </Typography>
                        </Box>
                    ) : (
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <CloudUploadIcon className={classes.uploadIcon}
                            // onClick={() => srtInputRef.current.click()} 
                            />
                            <Typography variant="body1" className={classes.instructions}>
                                <strong>Click to upload</strong> or drag and drop
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                SRT files only
                            </Typography>
                        </Box>
                    )}
                    <input
                        ref={srtInputRef}
                        type="file"
                        accept=".srt"
                        style={{ display: 'none' }}
                        onChange={handleSrtUpload}
                    />
                </Box>
            )}
            <KaraokeForm
                setIsEnabled={setIsEnabled}
                setKaraokeForm={setKaraokeForm}
                karaokeForm={karaokeForm}
                backingDuration={backingDuration}
                onVocalUpload={onVocalUpload}
                onVocalRemove={onVocalRemove}
                stickers={stickers}
                onStickerUpload={onStickerUpload}
                onAddLibrarySticker={onAddLibrarySticker}
                onRemoveSticker={onRemoveSticker}
                onClearStickers={onClearStickers}
                themeContext={themeContext}
                themeOptions={themeOptions}
                onPickTheme={onPickTheme}
                isEdit={isEdit}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                    className={classes.buttonStyle}
                    variant='contained'
                    disabled={lyrics.length === 0 || !isEnabled}
                    onClick={() => {
                        setSyncingLyrics(true)
                        updateAudioTime(0)
                        pauseAudio()
                    }}
                >
                    Sync Lyrics
                </Button>
                {onEditAssignments && (
                    <Button
                        className={classes.buttonStyle}
                        variant='contained'
                        disabled={lyrics.length === 0 || !isEnabled}
                        onClick={onEditAssignments}
                    >
                        Edit Lyric Assignments
                    </Button>
                )}
                {onSavePreview && (
                    <Button
                        className={classes.buttonStyle}
                        variant='contained'
                        disabled={!canSavePreview}
                        onClick={onSavePreview}
                    >
                        Save Preview
                    </Button>
                )}
            </Box>
            {onEditAssignments && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Saved lyrics are loaded. Edit assignments without re-syncing or retyping them.
                </Typography>
            )}
            {onSavePreview && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    Save Preview updates only the vocal clip and leaves lyric assignments as they are.
                </Typography>
            )}
        </>
    )
}

export default UploadLyrics
